import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";

// NOTE: This file is now used as the in-app dashboard component.
// Routing is handled by src/AppRouter.jsx

import { RefreshCw, Mail, TrendingUp, TrendingDown, AlertCircle, CheckCircle2, Image, X, Clock, Target, Info, Sparkles, Bell, BellRing, Send, ChevronLeft, ChevronRight, History, User, Activity, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Toaster, toast } from "sonner";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import headerLogo from "@/assets/bubble-radar-logo-header.png";
import {
  computeMostProbableTcWindow,
  sanitizeSyncMessage,
  buildAttachmentUrl,
  matchesAttachmentForSymbol,
  formatLocalBottomLabel,
  formatLocalBottomText,
  toFloat,
} from "@/utils/dashboardAppUtils";
import { LpplTimingBadge, LpplConfidenceBar } from "@/components/dashboard/LpplDisplay";
import { DashboardToolbar } from "@/components/dashboard/DashboardToolbar";
import { GraphModal } from "@/components/dashboard/GraphModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = headerLogo;
const LOGIN_BOOTSTRAP_KEY = "bubble-radar-login-bootstrap";


// -------------------- Market Risk Window (deterministic, no AI credits) --------------------
// Window width is fixed (UI option removed per request)
const MARKET_RISK_WINDOW_DAYS = 3;
// Still show the window for a few days after the TC passes (so it doesn't disappear the next day)
const MARKET_RISK_PAST_DAYS = 7;

function App() {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGraph, setSelectedGraph] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [crashRiskFilter, setCrashRiskFilter] = useState("All");
  // Market Risk Window width is fixed; option removed from UI
  const [alertSettings, setAlertSettings] = useState({
    enabled: false,
    email: "",
    min_risk_score: 3,
    min_confidence: 0.0,
    future_only: true,
    mlsl_required: false,
    alert_new_tickers: true,
    crash_risk_labels: []
  });
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [savingAlert, setSavingAlert] = useState(false);
  const [reportsList, setReportsList] = useState([]);
  const [currentReportIndex, setCurrentReportIndex] = useState(0);
  const [showReportHistory, setShowReportHistory] = useState(false);
  const [macroStackInfoOpen, setMacroStackInfoOpen] = useState(false);
  const [macroStackInfoSignal, setMacroStackInfoSignal] = useState(null);
  const [v2ExplainSymbol, setV2ExplainSymbol] = useState(null);
  const [watchlistDirectionFilter, setWatchlistDirectionFilter] = useState("all");
  const [watchlistOnlyNewConfirmed, setWatchlistOnlyNewConfirmed] = useState(false);

  const fetchAlertSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alerts/settings`, { withCredentials: true });
      setAlertSettings(response.data);
    } catch {
      setAlertSettings(null);
    }
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sync/status`, { withCredentials: true });
      setSyncStatus(response.data);
      return response.data;
    } catch {
      return null;
    }
  }, []);

  const fetchLatestReport = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/latest`, { withCredentials: true });
      setReport(response.data);
    } catch {
      setReport(null);
    }
  }, []);

  const fetchReportsList = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports`, { withCredentials: true });
      setReportsList(response.data || []);
    } catch {
      setReportsList([]);
    }
  }, []);

  const fetchReportById = useCallback(async (reportId) => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}`, { withCredentials: true });
      setReport(response.data);
    } catch {
      toast.error("Failed to load report");
    }
  }, []);

  const navigateReport = useCallback(async (direction) => {
    if (reportsList.length === 0) return;
    const newIndex = currentReportIndex + direction;
    if (newIndex < 0 || newIndex >= reportsList.length) return;
    
    setCurrentReportIndex(newIndex);
    const targetReport = reportsList[newIndex];
    if (targetReport?.id) {
      await fetchReportById(targetReport.id);
    }
  }, [reportsList, currentReportIndex, fetchReportById]);

  const goToLatestReport = useCallback(async () => {
    setCurrentReportIndex(0);
    await fetchLatestReport();
    toast.success("Loaded latest report");
  }, [fetchLatestReport]);

  useEffect(() => {
    // Defer initial state updates to avoid setState synchronously in the effect body
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      (async () => {
        setLoading(true);
        await Promise.all([fetchLatestReport(), fetchSyncStatus(), fetchAlertSettings(), fetchReportsList()]);
        setLoading(false);
      })();
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [fetchLatestReport, fetchSyncStatus, fetchAlertSettings, fetchReportsList]);

  const handleSaveAlertSettings = async () => {
    setSavingAlert(true);
    try {
      await axios.post(`${API}/alerts/settings`, alertSettings, { withCredentials: true });
      toast.success(alertSettings.enabled ? "Email alerts enabled" : "Email alerts disabled");
      setShowAlertSettings(false);
    } catch (e) {
      toast.error("Failed to save alert settings");
    }
    setSavingAlert(false);
  };

  const handleTestAlert = async () => {
    try {
      await axios.post(`${API}/alerts/test`, {}, { withCredentials: true });
      toast.success("Test email sent to your address");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test alert");
    }
  };

  const handleSignOut = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Continue with local cleanup even if server-side logout fails.
    }

    window.sessionStorage.removeItem(LOGIN_BOOTSTRAP_KEY);
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };

  const handleRowClick = (record) => {
    setSelectedRecord(record);
    
    if (report && report.attachments && report.attachments.length > 0) {
      // Also try to match horizon from record (e.g., "60 days" -> "60_days")
      const horizon = record.horizon?.replace(/\s+/g, '_').toLowerCase() || "";
      
      // Find all matching attachments for this symbol (excluding reversed versions first)
      const symbolAttachments = report.attachments.filter(att => {
        const filename = att.filename?.toLowerCase() || "";
        return matchesAttachmentForSymbol(filename, record.symbol) && !filename.includes('reversed');
      });
      
      // Try to find exact horizon match first
      let matchingAttachment = symbolAttachments.find(att => {


        const filename = att.filename?.toLowerCase() || "";
        return filename.includes(horizon);
      });
      
      // If no horizon match, take first non-reversed attachment for the symbol
      if (!matchingAttachment && symbolAttachments.length > 0) {
        matchingAttachment = symbolAttachments[0];
      }
      
      // Get all attachments for this symbol (including reversed and analysis charts) for selection
      const allSymbolAttachments = report.attachments.filter(att => {
        const filename = att.filename?.toLowerCase() || "";
        return matchesAttachmentForSymbol(filename, record.symbol);
      });
      
      if (matchingAttachment) {
        setSelectedGraph({
          url: buildAttachmentUrl(API, report.id, matchingAttachment.filename),
          filename: matchingAttachment.filename,
          record: record,
          allAttachments: allSymbolAttachments.length > 0 ? allSymbolAttachments : null,
          errorMessage: null,
        });
      } else {
        // Fallback: show modal with picker of available charts for this symbol
        setSelectedGraph({
          url: null,
          filename: null,
          record: record,
          noGraph: true,
          allAttachments: allSymbolAttachments.length > 0 ? allSymbolAttachments : null,
          errorMessage: `Plot unavailable for ${record.symbol || 'this ticker'}.`,
        });
      }
    } else {
      toast.info("No attachments available");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  const formatLastUpdate = (dateStr) => {
    if (!dateStr) return "Last update: N/A";
    try {
      const date = new Date(dateStr);
      const formatted = date
        .toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
        .replace(",", "");
      return `Last update: ${formatted}`;
    } catch {
      return "Last update: N/A";
    }
  };

  const _fmtMMMdd = (dateValue) => {
    if (!dateValue) return "N/A";
    try {
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
      return "N/A";
    }
  };

  const getRiskBadgeClass = (riskScore) => {
    if (!riskScore) return "status-badge status-badge-neutral";
    const score = parseInt(riskScore);
    if (score >= 4) return "status-badge status-badge-error";
    if (score >= 3) return "status-badge status-badge-warning";
    if (score >= 2) return "status-badge status-badge-neutral";
    return "status-badge status-badge-success";
  };

  const getInterpretationBadgeClass = (interpretation) => {
    if (!interpretation) return "status-badge status-badge-neutral";
    const i = interpretation.toLowerCase();
    if (i.includes("critical")) return "status-badge status-badge-error";
    if (i.includes("high")) return "status-badge status-badge-warning";
    if (i.includes("medium")) return "status-badge status-badge-neutral";
    return "status-badge status-badge-success";
  };

  const getDirectionIcon = (direction) => {
    if (!direction) return null;
    const d = direction.toLowerCase();
    if (d.includes("upward") || d.includes("up")) {
      return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    }
    if (d.includes("downward") || d.includes("down")) {
      return <TrendingDown className="w-4 h-4 text-red-500" />;
    }
    return <Activity className="w-4 h-4 text-slate-400" />;
  };

  const parseCrashRiskConfidence = (analysisText) => {
    if (!analysisText) return null;
    // Expected suffix appended by backend:
    // Crash Risk Confidence: 30% — HOLD ( ... )
    const match = String(analysisText).match(/Crash Risk Confidence:\s*(\d+)%\s*[—-]\s*([^\n(]+)(?:\s*\(([^\n]+)\))?/i);
    if (!match) return null;

    const score = parseInt(match[1], 10);
    const label = (match[2] || '').trim().toUpperCase();
    const summary = (match[3] || '').trim();

    if (Number.isNaN(score) || score < 0 || score > 100) return null;
    return { score, label, summary };
  };

  const getCrashRiskPillClass = (label) => {
    const l = (label || '').toUpperCase();
    if (l === 'BUY') return 'bg-emerald-100 text-emerald-700';
    if (l === 'HOLD') return 'bg-yellow-100 text-yellow-800';
    if (l === 'PREPARE SELL') return 'bg-orange-100 text-orange-800';
    if (l === 'SELL') return 'bg-red-100 text-red-700';
    if (l === 'CRASH ZONE') return 'bg-red-600 text-white';
    return 'bg-slate-100 text-slate-700';
  };

  const getTurnSignalPillClass = (label) => {
    const l = String(label || '').toUpperCase();
    if (l.includes('CONFIRMED')) return 'bg-red-600 text-white';
    if (l.includes('EARLY')) return 'bg-amber-100 text-amber-800';
    if (l.includes('WATCH')) return 'bg-sky-100 text-sky-800';
    return 'bg-slate-100 text-slate-700';
  };

  const getDirectionConfidenceMeta = (value) => {
    const confidence = Number(value);
    if (!Number.isFinite(confidence)) {
      return { label: 'N/A', className: 'bg-slate-100 text-slate-600' };
    }
    if (confidence >= 71) {
      return { label: 'High', className: 'bg-emerald-100 text-emerald-700' };
    }
    if (confidence >= 51) {
      return { label: 'Medium', className: 'bg-amber-100 text-amber-700' };
    }
    return { label: 'Low', className: 'bg-rose-100 text-rose-700' };
  };

  const getGateLabel = (gateKey) => {
    const map = {
      ichimoku: 'Ichimoku Trend Flip',
      wyckoff: 'Wyckoff Phase',
      macro: 'Macro Stack',
      lppl: 'LPPL Timing',
    };
    return map[gateKey] || gateKey;
  };

  const getGateStateClass = (passed) => {
    if (passed === true) return 'bg-rose-100 text-rose-700 border-rose-200';
    if (passed === false) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const toTestIdSuffix = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getLiquidationRiskMeta = (liq) => {
    if (!liq || typeof liq !== 'object') return null;
    const adj = Number(liq.adj || 0);
    const density = Number(liq.density_ratio || 0);
    const persistence = Number(liq.persistence || 0);

    let label = 'Neutral';
    let badgeClass = 'bg-slate-100 text-slate-700';
    if (adj >= 4) {
      label = 'Elevated';
      badgeClass = 'bg-red-100 text-red-700';
    } else if (adj >= 2) {
      label = 'Moderate';
      badgeClass = 'bg-amber-100 text-amber-800';
    } else if (adj <= -2) {
      label = 'Dampening';
      badgeClass = 'bg-emerald-100 text-emerald-700';
    }

    return { adj, density, persistence, label, badgeClass };
  };

  const formatMetric = (value, digits = 2) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return num.toFixed(digits);
  };

  const formatAbbrevNumber = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    const abs = Math.abs(num);
    if (abs >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toFixed(2);
  };

  const getAiAnalysisStyle = (analysis) => {
    if (!analysis) return { border: "border-slate-200", bg: "bg-slate-50", icon: "text-slate-400" };
    const a = analysis.toLowerCase();
    if (a.startsWith("supports")) {
      return { border: "border-emerald-200", bg: "bg-emerald-50", icon: "text-emerald-500" };
    }
    if (a.startsWith("contradicts")) {
      return { border: "border-red-200", bg: "bg-red-50", icon: "text-red-500" };
    }
    if (a.startsWith("mixed")) {
      return { border: "border-amber-200", bg: "bg-amber-50", icon: "text-amber-500" };
    }
    return { border: "border-blue-200", bg: "bg-blue-50", icon: "text-blue-500" };
  };

  // Map ticker symbols to display names
  const getDisplayName = (symbol) => {
    const nameMap = {
      'GC=F': 'GOLD',
      'SI=F': 'SILVER',
      'NOKEUR=X': 'NOKEUR',
      'CL=F': 'OIL',
      'NOKUSD=X': 'NOKUSD',
      'LLOY.L': 'LLOY'
    };
    return nameMap[symbol] || symbol;
  };

  // Heuristic categorization for filter buttons
  const getCategoryForSymbol = (symbol) => {
    const s = (symbol || "").toUpperCase();
    // Precious metals futures
    if (s === 'GC=F' || s === 'SI=F') return "Precious Metals";

    // Crypto pairs often appear as XXX-USD
    if (s.endsWith('-USD')) return "Cryptocurrency";

    return "Stocks";
  };

  const normalizeConfidencePct = (value) => {
    const parsed = toFloat(value);
    return parsed > 1 ? parsed : parsed * 100;
  };

  // Group records by symbol (only valid TC rows; hide incomplete tickers)
  const getGroupedRecords = () => {
    if (!report || !report.lppl_records) return {};
    const grouped = {};
    report.lppl_records
      .filter((r) => {
        if (!r) return false;
        if (r.placeholder) return false;
        const tc = String(r.tc_date || "").trim();
        if (!tc || tc.toUpperCase() === 'N/A') return false;
        return true;
      })
      .forEach((record) => {
        const symbol = record.symbol || 'UNKNOWN';
        if (!grouped[symbol]) {
          grouped[symbol] = [];
        }
        grouped[symbol].push(record);
      });
    return grouped;
  };

  const groupedRecords = useMemo(() => getGroupedRecords(), [report]);
  const symbols = useMemo(() => Object.keys(groupedRecords), [groupedRecords]);
  
  // Filter new_tickers / removed_tickers to only show tickers that are visible (have valid TC rows)
  const symbolsSet = useMemo(() => new Set(symbols), [symbols]);
  const visibleNewTickers = useMemo(() => (report?.new_tickers || []).filter(t => symbolsSet.has(t)), [report, symbolsSet]);
  const visibleRemovedTickers = useMemo(() => (report?.removed_tickers || []).filter(t => !symbolsSet.has(t)), [report, symbolsSet]);
  
  // Filter symbols based on search query + category filter
  const filteredSymbols = symbols.filter(symbol => {
    const matchesCategory = categoryFilter === "All" || getCategoryForSymbol(symbol) === categoryFilter;
    if (!matchesCategory) return false;

    const matchesCrash = crashRiskFilter === "All" || (() => {
      const m = (report?.crash_risk_by_symbol && report.crash_risk_by_symbol[symbol])
        || parseCrashRiskConfidence(report?.ai_analyses?.[symbol]);
      return m && m.label === crashRiskFilter;
    })();
    if (!matchesCrash) return false;

    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const displayName = getDisplayName(symbol).toLowerCase();
    return symbol.toLowerCase().includes(query) || displayName.includes(query);
  });

  const visibleGroupedRecords = useMemo(() => {
    const next = {};
    Object.entries(groupedRecords).forEach(([symbol, records]) => {
      next[symbol] = (records || []).filter((record) => normalizeConfidencePct(record.confidence) > 50);
    });
    return next;
  }, [groupedRecords]);

  const graphAttachmentChoices = useMemo(() => {
    if (!selectedGraph?.allAttachments?.length) return [];
    return selectedGraph.allAttachments.filter((att) => att.filename !== selectedGraph.filename).slice(0, 6);
  }, [selectedGraph]);


  // Market Risk Window (per visible ticker) — deterministic, no AI credits
  const lpplTimingBySymbol = useMemo(() => {
    const by = {};
    filteredSymbols.forEach((sym) => {
      const rows = groupedRecords[sym] || [];
      const res = computeMostProbableTcWindow(rows, MARKET_RISK_WINDOW_DAYS, MARKET_RISK_PAST_DAYS);
      if (res) by[sym] = res;
    });
    return by;
  }, [filteredSymbols, groupedRecords]);
  const crashRiskBySymbol = useMemo(() => {
    // Prefer backend deterministic crash risk mapping when available (can include liquidation modifiers)
    const backendMap = report?.crash_risk_by_symbol;
    if (backendMap && typeof backendMap === 'object') return backendMap;

    // Fallback: derive from AI analysis text (legacy behavior)
    const out = {};
    filteredSymbols.forEach((symbol) => {
      const m = parseCrashRiskConfidence(report?.ai_analyses?.[symbol]);
      if (m) out[symbol] = m;
    });
    return out;
  }, [filteredSymbols, report]);
  const crashRiskV2BySymbol = useMemo(() => {
    const backendMap = report?.crash_risk_v2_by_symbol;
    if (backendMap && typeof backendMap === 'object') return backendMap;
    return {};
  }, [report]);
  const localBottomBySymbol = useMemo(() => {
    const backendMap = report?.bottom_in_by_symbol;
    if (backendMap && typeof backendMap === 'object') return backendMap;
    return {};
  }, [report]);
  const topInBySymbol = useMemo(() => {
    const backendMap = report?.top_in_by_symbol;
    if (backendMap && typeof backendMap === 'object') return backendMap;
    return {};
  }, [report]);
  const turningWatchlist = useMemo(() => {
    const fromBackend = report?.turning_watchlist;
    if (Array.isArray(fromBackend) && fromBackend.length > 0) {
      return fromBackend;
    }

    const entries = [];
    symbols.forEach((symbol) => {
      const bottom = localBottomBySymbol?.[symbol];
      const top = topInBySymbol?.[symbol];
      if (bottom) {
        entries.push({
          symbol,
          direction: 'bottom',
          score: Number(bottom.score || 0),
          label: bottom.label,
          summary: bottom.summary,
          confirmed_signal: Boolean(bottom.confirmed_signal),
          early_signal: Boolean(bottom.early_signal),
          new_confirmed: false,
        });
      }
      if (top) {
        entries.push({
          symbol,
          direction: 'top',
          score: Number(top.score || 0),
          label: top.label,
          summary: top.summary,
          confirmed_signal: Boolean(top.confirmed_signal),
          early_signal: Boolean(top.early_signal),
          new_confirmed: false,
        });
      }
    });

    entries.sort((a, b) => {
      const bucket = (item) => {
        if (item.new_confirmed) return 0;
        if (item.confirmed_signal) return 1;
        if (item.early_signal) return 2;
        return 3;
      };
      const ba = bucket(a);
      const bb = bucket(b);
      if (ba !== bb) return ba - bb;
      if ((b.score || 0) !== (a.score || 0)) return (b.score || 0) - (a.score || 0);
      return String(a.symbol || '').localeCompare(String(b.symbol || ''));
    });

    return entries.slice(0, 24);
  }, [report, symbols, localBottomBySymbol, topInBySymbol]);
  const filteredTurningWatchlist = useMemo(() => {
    return turningWatchlist.filter((item) => {
      const direction = String(item?.direction || '').toLowerCase();
      if (watchlistDirectionFilter === 'top' && direction !== 'top') return false;
      if (watchlistDirectionFilter === 'bottom' && direction !== 'bottom') return false;
      if (watchlistOnlyNewConfirmed && !Boolean(item?.new_confirmed)) return false;
      return true;
    });
  }, [turningWatchlist, watchlistDirectionFilter, watchlistOnlyNewConfirmed]);
  const v2ExplainSnapshot = v2ExplainSymbol ? crashRiskV2BySymbol?.[v2ExplainSymbol] : null;
  const localBottomExplainSnapshot = v2ExplainSymbol ? localBottomBySymbol?.[v2ExplainSymbol] : null;
  const topExplainSnapshot = v2ExplainSymbol ? topInBySymbol?.[v2ExplainSymbol] : null;
  const v2ExplainConfidenceMeta = getDirectionConfidenceMeta(v2ExplainSnapshot?.outlook_confidence);

  const m2Global = report?.m2_global || null;
  const yieldCurve = report?.yield_curve || null;
  const dollarIndex = report?.dollar_index || null;
  const deathCrossBySymbol = report?.death_cross_by_symbol || {};

  const cryptoSymbols = useMemo(
    () => symbols.filter((sym) => String(sym || "").toUpperCase().endsWith("-USD")),
    [symbols]
  );

  const macroStackSymbol = useMemo(() => {
    const selectedSymbol = selectedRecord?.symbol;
    if (selectedSymbol && String(selectedSymbol).toUpperCase().endsWith("-USD")) {
      return selectedSymbol;
    }
    return cryptoSymbols[0] || null;
  }, [selectedRecord, cryptoSymbols]);

  const deathCrossEntry = macroStackSymbol ? deathCrossBySymbol?.[macroStackSymbol] : null;
  const deathCrossAvailable = Boolean(deathCrossEntry);
  const deathCrossActive = deathCrossAvailable ? Boolean(deathCrossEntry?.death_cross) : null;

  const macroStackIndicators = macroStackSymbol
    ? report?.indicators_by_symbol?.[macroStackSymbol]
    : null;
  const priceValue = Number(macroStackIndicators?.current_price);
  const ma50Value = Number(macroStackIndicators?.ema50 ?? macroStackIndicators?.ma50);
  const ma100Value = Number(macroStackIndicators?.ma100);
  const ma200Value = Number(macroStackIndicators?.ma200 ?? macroStackIndicators?.ema200);

  const priceBelow50Available = Number.isFinite(priceValue) && Number.isFinite(ma50Value);
  const priceBelow100Available = Number.isFinite(priceValue) && Number.isFinite(ma100Value);
  const priceBelow200Available = Number.isFinite(priceValue) && Number.isFinite(ma200Value);

  const priceBelow50 = priceBelow50Available ? priceValue < ma50Value : null;
  const priceBelow100 = priceBelow100Available ? priceValue < ma100Value : null;
  const priceBelow200 = priceBelow200Available ? priceValue < ma200Value : null;

  const m2Series = useMemo(() => {
    if (!m2Global) return [];
    const raw = Array.isArray(m2Global.series) ? m2Global.series : [];
    const cleaned = raw
      .map((item) => ({
        year: Number(item?.year),
        value: Number(item?.value),
      }))
      .filter((item) => Number.isFinite(item.year) && Number.isFinite(item.value));

    if (cleaned.length) {
      return cleaned.sort((a, b) => a.year - b.year).slice(-6);
    }

    if (Number.isFinite(Number(m2Global.value)) && Number.isFinite(Number(m2Global.year))) {
      return [{ year: Number(m2Global.year), value: Number(m2Global.value) }];
    }

    return [];
  }, [m2Global]);

  const m2Decelerating = useMemo(() => {
    if (!m2Series.length) return false;
    const desc = [...m2Series].sort((a, b) => b.year - a.year);
    if (!desc.length) return false;
    const latest = desc[0].value;
    const recent = desc.slice(0, 3);
    const avgRecent = recent.reduce((sum, row) => sum + row.value, 0) / recent.length;
    if (latest <= (avgRecent - 2.0)) {
      return true;
    }
    if (desc.length >= 3) {
      if (desc[0].value < desc[1].value && desc[1].value < desc[2].value) {
        return true;
      }
    }
    return false;
  }, [m2Series]);

  const m2Sparkline = useMemo(() => {
    if (!m2Series.length) return [];
    const values = m2Series.map((item) => item.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const denom = max - min || 1;
    return m2Series.map((item, idx) => ({
      ...item,
      height: Math.round(6 + ((item.value - min) / denom) * 14),
      isLast: idx === m2Series.length - 1,
    }));
  }, [m2Series]);

  const m2Latest = m2Series.length ? m2Series[m2Series.length - 1] : null;
  const m2SeriesDesc = useMemo(() => [...m2Series].sort((a, b) => b.year - a.year), [m2Series]);

  const yieldCurveInverted = Boolean(yieldCurve?.is_inverted);
  const dollarStrengthening = Boolean(dollarIndex?.is_strengthening);
  const macroStackAvailable = Boolean(
    yieldCurve 
    && m2Series.length
    && dollarIndex
    && Object.keys(dollarIndex).length
    && deathCrossAvailable
    && Number.isFinite(priceValue)
  );

  const macroStackSignals = useMemo(() => ([
    {
      key: "yield-curve",
      label: "Yield Curve",
      fullLabel: "Yield Curve Inversion",
      active: yieldCurveInverted,
      bullish: "A normal or steepening curve generally supports growth and risk appetite.",
      bearish: "An inverted curve points to late-cycle stress and tighter credit conditions.",
      risk: "Inversion typically raises downside risk for risk assets.",
    },
    {
      key: "liquidity",
      label: "Liquidity (M2)",
      fullLabel: "Liquidity Contracting (M2)",
      active: m2Decelerating,
      bullish: "Expanding liquidity often supports risk-taking and trend continuation.",
      bearish: "Contracting liquidity can pressure speculative assets and risk appetite.",
      risk: "Tighter liquidity tends to elevate volatility and drawdown risk.",
    },
    {
      key: "dollar",
      label: "Dollar Strength",
      fullLabel: "Dollar Strengthening",
      active: dollarStrengthening,
      bullish: "A softer dollar can ease global financial conditions and support risk assets.",
      bearish: "A strengthening dollar can tighten conditions and weigh on risk assets.",
      risk: "Sustained dollar strength can increase downside risk in crypto.",
    },
    {
      key: "death-cross",
      label: "3-Day Death Cross",
      fullLabel: "3-Day Death Cross",
      active: deathCrossActive,
      bullish: "Short-term trend holding above the longer-term trend suggests stability.",
      bearish: "A bearish crossover signals weakening momentum.",
      risk: "Often precedes deeper pullbacks and raises drawdown risk.",
    },
    {
      key: "below-200",
      label: "Below 200D MA",
      fullLabel: "Price below 200-day MA",
      active: priceBelow200,
      bullish: "Price above the long-term average suggests the primary trend is intact.",
      bearish: "Below the long-term average indicates trend weakness.",
      risk: "Below the long-term average often increases downside risk.",
    },
    {
      key: "below-100",
      label: "Below 100D MA",
      fullLabel: "Price below 100-day MA",
      active: priceBelow100,
      bullish: "Holding above the medium-term average suggests healthy trend structure.",
      bearish: "Falling below the medium-term average indicates weakening trend support.",
      risk: "Below the medium-term average can raise near-term drawdown risk.",
    },
    {
      key: "below-50",
      label: "Below 50D MA",
      fullLabel: "Price below 50-day MA",
      active: priceBelow50,
      bullish: "Price above the short-to-medium average supports positive momentum.",
      bearish: "Below the short-to-medium average signals fading momentum.",
      risk: "Below the short-to-medium average can raise short-term risk.",
    },
  ]), [yieldCurveInverted, m2Decelerating, dollarStrengthening, deathCrossActive, priceBelow200, priceBelow100, priceBelow50]);

  const macroStackInfoByKey = useMemo(() => {
    const map = {};
    macroStackSignals.forEach((signal) => {
      map[signal.key] = signal;
    });
    return map;
  }, [macroStackSignals]);

  const handleMacroStackSignalClick = useCallback((signal) => {
    setMacroStackInfoSignal(signal);
    setMacroStackInfoOpen(true);
  }, []);

  return (
    <div className="dashboard-container">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="dashboard-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-center gap-3 min-w-0" data-testid="dashboard-header-brand">
              <div className="w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center shrink-0">
                <button
                  type="button"
                  data-testid="header-logo-button"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="flex items-center justify-center w-full h-full"
                  aria-label="Scroll to top"
                >
                  <img
                    src={LOGO_URL}
                    alt="Bubble Radar logo"
                    data-testid="header-logo"
                    className="w-full h-full object-contain"
                  />
                </button>
              </div>
              <div className="min-w-0">
                <h1
                  data-testid="dashboard-title"
                  className="text-lg sm:text-xl font-semibold text-slate-900 tracking-tight"
                >
                  Bubble Radar
                </h1>
                <p
                  data-testid="dashboard-subtitle"
                  className="text-sm text-slate-500 leading-snug"
                >
                  Financial Analysis Platform
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center justify-start xl:justify-end gap-2 sm:gap-3">
              {/* Sync Status */}
              <div
                data-testid="sync-status-indicator"
                className={`sync-indicator ${syncStatus?.status === "syncing" ? "syncing" : ""}`}
              >
                {syncStatus?.status === "syncing" ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{sanitizeSyncMessage(syncStatus?.message, "Syncing...")}</span>
                  </>
                ) : syncStatus?.status === "queued" ? (
                  <>
                    <Clock className="w-4 h-4" />
                    <span>{sanitizeSyncMessage(syncStatus?.message, "Sync queued")}</span>
                  </>
                ) : syncStatus?.status === "error" ? (
                  <>
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-red-500">Error</span>
                  </>
                ) : syncStatus?.last_sync ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    <span>Last sync: {formatDate(syncStatus.last_sync)}</span>
                  </>
                ) : (
                  <>
                    <Clock className="w-4 h-4" />
                    <span>Not synced yet</span>
                  </>
                )}
              </div>

              <Button
                data-testid="header-profile-button"
                variant="outline"
                onClick={() => navigate("/profile")}
                className="border-slate-200 text-slate-600"
              >
                <User className="w-4 h-4 mr-2" />
                My Profile
              </Button>
              
              {/* Alert Settings Button */}
              <Button
                data-testid="alert-settings-btn"
                variant="outline"
                onClick={() => setShowAlertSettings(true)}
                className={`border-slate-200 ${alertSettings.enabled ? 'text-amber-600 border-amber-200 bg-amber-50' : 'text-slate-600'}`}
              >
                {alertSettings.enabled ? <BellRing className="w-4 h-4 mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                Alerts {alertSettings.enabled ? "On" : "Off"}
              </Button>

              <Button
                data-testid="header-signout-button"
                variant="outline"
                onClick={handleSignOut}
                className="border-slate-200 text-slate-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : !report || !report.lppl_records || report.lppl_records.length === 0 ? (
          <div className="dashboard-card">
            <div className="empty-state">
              <Mail className="empty-state-icon" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Analysis Data</h3>
              <p className="text-sm text-slate-500 mb-4">
                Click &quot;Refresh&quot; to fetch the latest Daily Comprehensive Analysis report.
              </p>
              
            </div>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            {/* Report Info Card */}
            <div className="dashboard-card p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-medium text-slate-900">
                      {report.email_subject || "Daily Comprehensive Analysis"}
                    </h2>
                    <div className="flex flex-col gap-2 text-sm text-slate-500">
                      <span data-testid="report-last-update" className="text-sm text-slate-500">
                        {formatLastUpdate(report.email_date)}
                      </span>
                      {/* Macro Context Box Removed */}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-semibold text-slate-900 font-mono">
                    {report.lppl_records?.length || 0}
                  </span>
                  <p
                    data-testid="tc-detections-label"
                    className="text-xs text-slate-500 uppercase tracking-wider"
                  >
                    EARLY WARNING SIGNALS (TC)
                  </p>
                </div>
              </div>
              
              {/* Historical Report Navigation */}
              {reportsList.length > 1 && (
                <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <History className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-500">
                      Report {currentReportIndex + 1} of {reportsList.length}
                    </span>
                    {currentReportIndex > 0 && (
                      <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                        Historical
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateReport(1)}
                      disabled={currentReportIndex >= reportsList.length - 1}
                      className="h-7 px-2 text-xs"
                    >
                      <ChevronLeft className="w-3 h-3 mr-1" />
                      Older
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateReport(-1)}
                      disabled={currentReportIndex <= 0}
                      className="h-7 px-2 text-xs"
                    >
                      Newer
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                    {currentReportIndex > 0 && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={goToLatestReport}
                        className="h-7 px-2 text-xs"
                      >
                        Latest
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {/* New Tickers Alert */}
              {visibleNewTickers.length > 0 && (
                <div className="mx-4 mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-600 font-medium text-sm">🆕 New Tickers:</span>
                    <span className="text-emerald-700 font-mono text-sm">{visibleNewTickers.map(t => getDisplayName(t)).join(', ')}</span>
                  </div>
                </div>
              )}
              
              {/* Removed Tickers Info */}
              {visibleRemovedTickers.length > 0 && (
                <div className="mx-4 mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium text-sm">Removed:</span>
                    <span className="text-slate-600 font-mono text-sm">{visibleRemovedTickers.map(t => getDisplayName(t)).join(', ')}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Bubble Metrics Explanation - Always visible */}
            <div className="dashboard-card overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  <span className="font-medium text-slate-700">Bubble Metrics Explanation</span>
                </div>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {/* Market Risk Window */}
                  <div className="p-3 bg-slate-50 rounded-lg border-l-4 border-sky-500">
                    <h4 className="font-semibold text-slate-800 text-sm">Market Risk Window</h4>
                    <p className="text-xs text-slate-600 mt-1">Estimates the most probable TC date window from the LPPL rows.</p>
                  </div>
                  {/* Liquidation Snapshot */}
                  <div
                    data-testid="bubble-metrics-liquidation-card"
                    className="p-3 bg-slate-50 rounded-lg border-l-4 border-amber-500"
                  >
                    <div className="flex items-center gap-1">
                      <h4
                        data-testid="bubble-metrics-liquidation-title"
                        className="font-semibold text-slate-800 text-sm"
                      >
                        Liquidation Snapshot
                      </h4>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              data-testid="liquidation-tooltip-trigger"
                              className="text-slate-400 hover:text-slate-600"
                            >
                              <Info className="w-3 h-3" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            data-testid="liquidation-tooltip-content"
                            side="top"
                            className="max-w-xs text-xs"
                          >
                            Density spikes and aligned imbalance can raise the crash-risk score; thin liquidity or opposing flow can dampen it.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p
                      data-testid="bubble-metrics-liquidation-description"
                      className="text-xs text-slate-600 mt-1"
                    >
                      Labels: Dampening = reduces risk score, Neutral = no impact, Moderate = modest lift, Elevated = strong lift.
                    </p>
                  </div>
                  {/* Wyckoff Institutional Confidence */}
                  <div
                    data-testid="bubble-metrics-wyckoff-card"
                    className="p-3 bg-slate-50 rounded-lg border-l-4 border-violet-500"
                  >
                    <h4 className="font-semibold text-slate-800 text-sm">Wyckoff Institutional Confidence Risk</h4>
                    <p className="text-xs text-slate-600 mt-1">
                      Institutional Wyckoff phase + macro filter score highlighting accumulation/distribution quality.
                    </p>
                  </div>
                  
                  {/* TC Date */}
                  <div className="p-3 bg-slate-50 rounded-lg border-l-4 border-blue-500">
                    <h4 className="font-semibold text-slate-800 text-sm">TC Date</h4>
                    <p className="text-xs text-slate-600 mt-1">Critical time when bubble may burst. Closer date = higher risk.</p>
                  </div>
                  
                  {/* Confidence */}
                  <div className="p-3 bg-slate-50 rounded-lg border-l-4 border-blue-500">
                    <h4 className="font-semibold text-slate-800 text-sm">Confidence</h4>
                    <p className="text-xs text-slate-600 mt-1">Proportion of reliable LPPLS fits. Higher = more consistent bubble detection.</p>
                  </div>
                  
                  
                  {/* Risk Score */}
                  <div className="p-3 bg-slate-50 rounded-lg border-l-4 border-blue-500">
                    <h4 className="font-semibold text-slate-800 text-sm">Risk Score (1-4)</h4>
                    <p className="text-xs text-slate-600 mt-1">CPI-based: 1=Low, 2=Medium, 3=High, 4=Critical instability.</p>
                  </div>
                  
                  {/* Bubble Strength */}
                  <div className="p-3 bg-slate-50 rounded-lg border-l-4 border-blue-500">
                    <h4 className="font-semibold text-slate-800 text-sm">Bubble Strength</h4>
                    <p className="text-xs text-slate-600 mt-1">From exponent m: Strong (m&lt;0.3), Moderate (0.3-0.6), Weak (&gt;0.6).</p>
                  </div>
                  
                  {/* R² */}
                  <div className="p-3 bg-slate-50 rounded-lg border-l-4 border-emerald-500">
                    <h4 className="font-semibold text-slate-800 text-sm">R² (Fit Quality)</h4>
                    <p className="text-xs text-slate-600 mt-1">Variance explained by model. <span className="text-emerald-600 font-medium">≥0.9 excellent</span>, ≥0.8 good, &lt;0.8 weak.</p>
                  </div>
                  
                  {/* RMSE */}
                  <div className="p-3 bg-slate-50 rounded-lg border-l-4 border-emerald-500">
                    <h4 className="font-semibold text-slate-800 text-sm">RMSE (Error)</h4>
                    <p className="text-xs text-slate-600 mt-1">Deviation from actual price. <span className="text-emerald-600 font-medium">≤0.03 excellent</span>, ≤0.05 good, &gt;0.05 weak.</p>
                  </div>
                  
                  {/* Direction */}
                  <div className="p-3 bg-slate-50 rounded-lg border-l-4 border-blue-500">
                    <h4 className="font-semibold text-slate-800 text-sm">Direction</h4>
                    <p className="text-xs text-slate-600 mt-1">Upward = blow-off top expected. Downward = crash expected.</p>
                  </div>

                  
                                  </div>
              </div>
            </div>

            {/* Crash Risk Confidence Explanation */}
            <div className="dashboard-card overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span className="font-medium text-slate-700">Crash Risk Confidence (0–100%)</span>
                </div>
              </div>
              <div className="p-4 space-y-4">
                <p className="text-sm text-slate-600">
                  A blended confidence score estimating how likely the market is entering a downside / LPPL crash phase.
                  <span className="ml-1 text-slate-500">0% = bullish continuation, 100% = crash zone / TC alignment.</span>
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">EMA structure</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">Price vs EMA21/50/200 (breakdown adds risk).</p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">RSI</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">Momentum loss as RSI falls below ~55/50/45.</p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">CMF</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">Capital flow; negative CMF increases risk.</p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">MACD</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">Bearish histogram + MACD below signal increases risk.</p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">ADX</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">Trend exhaustion; low ADX raises risk of reversal.</p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">LPPL time-to-TC</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">Closer TC date increases risk (e.g., &lt;14 days highest).</p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">LPPL confidence</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">Higher LPPL fit confidence increases crash-phase confidence.</p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">Bollinger Bands</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">Overextension risk when price is near/above the upper band.</p>
                  </div>

                  {/* V2 Explanation Cards Removed */}

                </div>

                <div className="flex flex-wrap gap-2">
                  {[{
                    label: 'BUY',
                    text: '0–20 BUY',
                    cls: 'bg-emerald-100 text-emerald-700'
                  }, {
                    label: 'HOLD',
                    text: '21–40 HOLD',
                    cls: 'bg-yellow-100 text-yellow-800'
                  }, {
                    label: 'PREPARE SELL',
                    text: '41–60 PREPARE SELL',
                    cls: 'bg-orange-100 text-orange-800'
                  }, {
                    label: 'SELL',
                    text: '61–80 SELL',
                    cls: 'bg-red-100 text-red-700'
                  }, {
                    label: 'CRASH ZONE',
                    text: '81–100 CRASH ZONE',
                    cls: 'bg-red-600 text-white'
                  }].map((b) => (
                    <button
                      key={b.label}
                      onClick={() => setCrashRiskFilter(crashRiskFilter === b.label ? 'All' : b.label)}
                      className={`inline-flex items-center justify-center h-7 px-2 rounded-md text-xs font-medium leading-none whitespace-nowrap transition-colors ${b.cls} ${crashRiskFilter === b.label ? 'ring-2 ring-slate-900 ring-offset-2' : 'hover:opacity-90'}`}
                      title={crashRiskFilter === b.label ? 'Click to clear filter' : `Filter tickers by ${b.label}`}
                    >
                      {b.text}
                    </button>
                  ))}
                </div>
              </div>
            </div>


            <DashboardToolbar
              crashRiskFilter={crashRiskFilter}
              setCrashRiskFilter={setCrashRiskFilter}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              categoryFilter={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              filteredSymbolsCount={filteredSymbols.length}
              totalSymbolsCount={symbols.length}
            />

            {/* Top/Bottom Watchlist Removed per user request */}
            {/*
            {turningWatchlist.length > 0 ? (
              <div className="dashboard-card p-4" data-testid="top-bottom-watchlist-panel">
                ... (removed content)
              </div>
            ) : null}
            */}

            {/* LPPL Data Tables by Symbol */}
            {filteredSymbols.map((symbol) => {
              const symbolTestId = toTestIdSuffix(symbol);
              const riskSnapshot = crashRiskBySymbol?.[symbol];
              const riskSnapshotV2 = crashRiskV2BySymbol?.[symbol];
              const localBottomSnapshot = localBottomBySymbol?.[symbol];
              const topSnapshot = topInBySymbol?.[symbol];
              const liqMeta = getLiquidationRiskMeta(riskSnapshot?.liq);
              const liqFeature = report?.liquidation_features_by_symbol?.[symbol];
              const liqHasData = liqFeature && typeof liqFeature === 'object';
              const liqEvents = liqHasData ? liqFeature.events : null;
              const liqImbalance = liqHasData ? liqFeature.imbalance : null;
              const liqWallUp = liqHasData ? liqFeature.liq_wall_up : null;
              const liqWallDown = liqHasData ? liqFeature.liq_wall_down : null;
              const liqTotal = liqHasData ? liqFeature.total_notional : null;
              const wyckoffSnapshot = report?.wyckoff_by_symbol?.[symbol];
              const wyckoffConfidence = Number(wyckoffSnapshot?.institutional_confidence);
              const wyckoffPhase = wyckoffSnapshot?.phase_label;
              const wyckoffCycle = wyckoffSnapshot?.phase_cycle;
              const wyckoffBottom = wyckoffSnapshot?.likely_bottom?.date;
              const wyckoffMacro = wyckoffSnapshot?.macro_regime;
              const wyckoffAvailable = wyckoffSnapshot && wyckoffSnapshot?.status !== "insufficient_data";
              const adjustedScore = Number.isFinite(Number(riskSnapshot?.score))
                ? Math.round(Number(riskSnapshot?.score))
                : null;
              const adjustedScoreV2 = Number.isFinite(Number(riskSnapshotV2?.score))
                ? Math.round(Number(riskSnapshotV2?.score))
                : null;
              const v2ConfidenceMeta = getDirectionConfidenceMeta(riskSnapshotV2?.outlook_confidence);
              const localBottomScore = Number.isFinite(Number(localBottomSnapshot?.score))
                ? Math.round(Number(localBottomSnapshot?.score))
                : null;
              const topScore = Number.isFinite(Number(topSnapshot?.score))
                ? Math.round(Number(topSnapshot?.score))
                : null;
              const symbolIndicators = report?.indicators_by_symbol?.[symbol] || {};
              const symbolPrice = Number(symbolIndicators?.current_price);
              const symbolMa50 = Number(symbolIndicators?.ema50 ?? symbolIndicators?.ma50);
              const symbolMa100 = Number(symbolIndicators?.ma100);
              const symbolMa200 = Number(symbolIndicators?.ma200 ?? symbolIndicators?.ema200);
              const symbolBelow50 = Number.isFinite(symbolPrice) && Number.isFinite(symbolMa50)
                ? symbolPrice < symbolMa50
                : null;
              const symbolBelow100 = Number.isFinite(symbolPrice) && Number.isFinite(symbolMa100)
                ? symbolPrice < symbolMa100
                : null;
              const symbolBelow200 = Number.isFinite(symbolPrice) && Number.isFinite(symbolMa200)
                ? symbolPrice < symbolMa200
                : null;
              const symbolDeathCross = report?.death_cross_by_symbol?.[symbol];
              const symbolDeathCrossActive = symbolDeathCross ? Boolean(symbolDeathCross?.death_cross) : null;
              const tickerMacroSignals = [
                {
                  key: "death-cross",
                  label: "3-Day Death Cross",
                  active: symbolDeathCrossActive,
                },
                {
                  key: "below-200",
                  label: "Below 200D MA",
                  active: symbolBelow200,
                },
                {
                  key: "below-100",
                  label: "Below 100D MA",
                  active: symbolBelow100,
                },
                {
                  key: "below-50",
                  label: "Below 50D MA",
                  active: symbolBelow50,
                },
              ].map((signal) => ({
                ...signal,
                ...(macroStackInfoByKey[signal.key] || {}),
                active: signal.active,
              }));
              return (
              <div key={symbol} className="dashboard-card overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-slate-900">{getDisplayName(symbol)}</span>
                    {getDisplayName(symbol) !== symbol && (
                      <span className="text-xs text-slate-400">({symbol})</span>
                    )}
                    <span className="text-xs text-slate-500">
                      ({groupedRecords[symbol].length} TC{groupedRecords[symbol].length > 1 ? 's' : ''})
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">
                    Click row to view graph
                  </span>
                </div>
                <ScrollArea className="w-full">
                  <div className="min-w-[1000px]">
                    <Table className="table-fixed w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[90px] sticky left-0 bg-slate-50 z-10">TC Date</TableHead>
                          <TableHead className="w-[70px]">Timeframe</TableHead>
                          <TableHead className="w-[50px]">Conf.</TableHead>
                          <TableHead className="w-[45px]">Risk</TableHead>
                          <TableHead className="w-[65px]">Interp.</TableHead>
                          <TableHead className="w-[80px]">Direction</TableHead>
                          <TableHead className="w-[65px]">Strength</TableHead>
                          <TableHead className="w-[55px]">R²</TableHead>
                          <TableHead className="w-[55px]">RMSE</TableHead>
                                                    <TableHead className="w-[35px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(visibleGroupedRecords[symbol] || [])
                          .map((record, idx) => (
                          <TableRow
                            key={record.id || `${symbol}-${record.tc_date}-${record.horizon}-${record.direction}-${record.risk_score}`}
                            data-testid={`data-row-${record.id || idx}`}
                            className={`clickable cursor-pointer table-row-interactive ${!record.is_future ? 'opacity-50' : ''}`}
                            onClick={() => handleRowClick(record)}
                          >
                            <TableCell className={`font-mono font-medium sticky left-0 z-10 ${record.is_future ? 'text-slate-900 bg-white' : 'text-slate-400 bg-slate-50'}`}>
                              {record.tc_date || "N/A"}
                              {!record.is_future && <span className="ml-1 text-[10px] text-slate-400">(past)</span>}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-slate-600">
                              {record.horizon || "—"}
                            </TableCell>
                            <TableCell className="font-mono text-slate-700">
                              {record.confidence || "—"}
                            </TableCell>
                            <TableCell>
                              {record.risk_score ? (
                                <span className={getRiskBadgeClass(record.risk_score)}>
                                  {record.risk_score}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {record.interpretation ? (
                                <span className={getInterpretationBadgeClass(record.interpretation)}>
                                  {record.interpretation}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {getDirectionIcon(record.direction)}
                                <span className="text-xs text-slate-600">
                                  {record.direction?.replace('Crash', '')?.trim() || "—"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-xs text-slate-600">
                                {record.bubble_strength || "—"}
                              </span>
                            </TableCell>
                            <TableCell>
                              {record.r_squared ? (
                                <span className={`font-mono text-xs ${parseFloat(record.r_squared) >= 0.9 ? 'text-emerald-600 font-medium' : parseFloat(record.r_squared) >= 0.8 ? 'text-slate-700' : 'text-orange-600'}`}>
                                  {record.r_squared}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {record.rmse ? (
                                <span className={`font-mono text-xs ${parseFloat(record.rmse) <= 0.03 ? 'text-emerald-600 font-medium' : parseFloat(record.rmse) <= 0.05 ? 'text-slate-700' : 'text-orange-600'}`}>
                                  {record.rmse}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                                                        <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-500 hover:text-slate-900 p-1"
                              >
                                <Image className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
                
                {/* AI Analysis Box */}
                {report.ai_analyses && (
                  <div className={`mx-4 mb-4 p-4 rounded-lg border ${getAiAnalysisStyle(report.ai_analyses?.[symbol]).border} ${getAiAnalysisStyle(report.ai_analyses?.[symbol]).bg}`}>
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 mt-0.5 ${getAiAnalysisStyle(report.ai_analyses?.[symbol]).icon}`}>
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-800 text-sm">AI Analysis</span>
                          {(() => {
                            const m = crashRiskBySymbol?.[symbol] || parseCrashRiskConfidence(report?.ai_analyses?.[symbol]);
                            if (!m) return null;
                            return (
                              <span
                                title={m.summary ? m.summary : undefined}
                                className={`ml-1 px-2 py-0.5 rounded-md text-xs font-medium ${getCrashRiskPillClass(m.label)}`}
                              >
                                {m.score}% {m.label}
                              </span>
                            );
                          })()}
                          {riskSnapshotV2 ? (
                            <span
                              data-testid={`crash-risk-v2-pill-${symbolTestId}`}
                              title={riskSnapshotV2.summary_human || riskSnapshotV2.summary ? `V2: ${riskSnapshotV2.summary_human || riskSnapshotV2.summary}` : undefined}
                              className={`ml-1 px-2 py-0.5 rounded-md text-xs font-medium ${getCrashRiskPillClass(riskSnapshotV2.label)}`}
                            >
                              V2 {Math.round(Number(riskSnapshotV2.score || 0))}% {riskSnapshotV2.label}
                            </span>
                          ) : null}
                          <span className="text-xs text-slate-500">Gemini</span>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {formatLocalBottomText(report.ai_analyses?.[symbol] || "AI analysis unavailable for this report.")}
                        </p>
                        <p
                          data-testid={`ai-liquidation-summary-${symbolTestId}`}
                          className="text-xs text-slate-500 mt-2"
                        >
                          {liqHasData
                            ? `Liquidation snapshot: ${liqMeta?.label || 'Neutral'} • Events ${liqEvents ?? '—'} • Imbalance ${formatMetric(liqImbalance)} • Walls ${formatAbbrevNumber(liqWallUp)} / ${formatAbbrevNumber(liqWallDown)} • Total ${formatAbbrevNumber(liqTotal)}`
                            : "Liquidation snapshot: not available."}
                        </p>
                      </div>


                    </div>
                  </div>
                )}


                {/* Market Risk Window (Most probable TC window) */}
                {lpplTimingBySymbol[symbol] && (
                  <div className="mx-4 mb-4 -mt-1 p-3 rounded-lg border border-slate-200 bg-white">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-700">Market Risk Window</span>
                          <LpplTimingBadge value={lpplTimingBySymbol[symbol].badge} />
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <LpplConfidenceBar
                          value={lpplTimingBySymbol[symbol].confPct}
                          label="LPPL confidence"
                          testId={`lppl-confidence-${symbolTestId}`}
                        />
                        <LpplConfidenceBar
                          value={adjustedScore !== null ? adjustedScore : lpplTimingBySymbol[symbol].confPct}
                          label={adjustedScore !== null ? "Adjusted risk" : "LPPL confidence"}
                          testId={`market-risk-score-${symbolTestId}`}
                        />
                        {adjustedScoreV2 !== null ? (
                          <LpplConfidenceBar
                            value={adjustedScoreV2}
                            label="Confluence risk (V2)"
                            testId={`market-risk-v2-score-${symbolTestId}`}
                          />
                        ) : null}
                        {localBottomScore !== null ? (
                          <LpplConfidenceBar
                            value={localBottomScore}
                            label="Local Bottom In score"
                            testId={`bottom-in-score-${symbolTestId}`}
                          />
                        ) : null}
                        {topScore !== null ? (
                          <LpplConfidenceBar
                            value={topScore}
                            label="Top In score"
                            testId={`top-in-score-${symbolTestId}`}
                          />
                        ) : null}
                        {wyckoffAvailable ? (
                          <LpplConfidenceBar
                            value={wyckoffConfidence}
                            label="Wyckoff institutional risk"
                            testId={`wyckoff-confidence-${symbolTestId}`}
                          />
                        ) : (
                          <span
                            data-testid={`wyckoff-confidence-${symbolTestId}-empty`}
                            className="text-[10px] text-slate-400"
                          >
                            Wyckoff data unavailable
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-slate-700">
                      Most probable TC window: <span className="font-medium">~ {_fmtMMMdd(lpplTimingBySymbol[symbol].windowStart)}–{_fmtMMMdd(lpplTimingBySymbol[symbol].windowEnd)}</span>
                      <span className="text-slate-500">
                        {lpplTimingBySymbol[symbol].daysToCenter >= 0
                          ? ` (TC in ${Math.round(lpplTimingBySymbol[symbol].daysToCenter)} days)`
                          : ` (TC was ${Math.abs(Math.round(lpplTimingBySymbol[symbol].daysToCenter))} days ago)`}
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Consensus {lpplTimingBySymbol[symbol].consensusPct}% • Avg confidence {lpplTimingBySymbol[symbol].avgConfPct}%
                    </p>
                    {riskSnapshotV2 ? (
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <p
                          data-testid={`crash-risk-v2-summary-${symbolTestId}`}
                          className="text-xs text-slate-500"
                        >
                          V2 model: {riskSnapshotV2.summary_human || riskSnapshotV2.summary || "Confluence model active"}
                        </p>
                        <span
                          data-testid={`v2-direction-confidence-${symbolTestId}`}
                          className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${v2ConfidenceMeta.className}`}
                        >
                          Direction confidence: {Number.isFinite(Number(riskSnapshotV2?.outlook_confidence)) ? `${Math.round(Number(riskSnapshotV2?.outlook_confidence))}%` : 'N/A'} ({v2ConfidenceMeta.label})
                        </span>
                        <span
                          data-testid={`v2-direction-confidence-legend-${symbolTestId}`}
                          className="text-[10px] text-slate-500"
                        >
                          Legend: 35–50 Low • 51–70 Medium • 71+ High
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid={`crash-risk-v2-explain-button-${symbolTestId}`}
                          className="h-6 px-2 text-[10px]"
                          onClick={() => setV2ExplainSymbol(symbol)}
                        >
                          Why this V2 score?
                        </Button>
                      </div>
                    ) : null}
                    {(localBottomSnapshot || topSnapshot) ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`turn-signals-${symbolTestId}`}>
                        {localBottomSnapshot ? (
                          <span
                            data-testid={`bottom-in-badge-${symbolTestId}`}
                            className={`px-2 py-0.5 rounded-md text-xs font-medium ${getTurnSignalPillClass(localBottomSnapshot?.label)}`}
                            title={localBottomSnapshot?.summary || undefined}
                          >
                            Local Bottom: {formatLocalBottomLabel(localBottomSnapshot?.label || 'N/A')}
                          </span>
                        ) : null}
                        {topSnapshot ? (
                          <span
                            data-testid={`top-in-badge-${symbolTestId}`}
                            className={`px-2 py-0.5 rounded-md text-xs font-medium ${getTurnSignalPillClass(topSnapshot?.label)}`}
                            title={topSnapshot?.summary || undefined}
                          >
                            Top: {topSnapshot?.label || 'N/A'}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    {liqMeta ? (
                      <div
                        data-testid={`liquidation-snapshot-${symbolTestId}`}
                        className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600"
                      >
                        <span className="font-medium text-slate-700">Liquidation snapshot</span>
                        <span
                          data-testid={`liquidation-risk-badge-${symbolTestId}`}
                          className={`px-2 py-0.5 rounded-md text-xs font-medium ${liqMeta.badgeClass}`}
                        >
                          {liqMeta.label}
                        </span>
                      </div>
                    ) : (
                      <p
                        data-testid={`liquidation-snapshot-${symbolTestId}`}
                        className="mt-2 text-xs text-slate-400"
                      >
                        Liquidation snapshot not available.
                      </p>
                    )}
                    <div
                      data-testid={`ticker-macro-stack-${symbolTestId}`}
                      className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1"
                    >
                      <span className="text-[11px] font-semibold uppercase text-slate-500">Macro View (Long Term)</span>
                      {tickerMacroSignals.map((signal) => (
                        <button
                          key={signal.key}
                          type="button"
                          data-testid={`ticker-macro-signal-${symbolTestId}-${signal.key}`}
                          onClick={() => handleMacroStackSignalClick(signal)}
                          className={`px-2 py-0.5 rounded-full border text-[10px] font-medium transition-colors ${signal.active === null || signal.active === undefined ? "border-slate-200 bg-white text-slate-400" : signal.active ? "border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300" : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300"}`}
                        >
                          {signal.label}
                        </button>
                      ))}
                    </div>
                    <div
                      data-testid={`wyckoff-summary-${symbolTestId}`}
                      className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600"
                    >
                      <span className="font-medium text-slate-700">Wyckoff</span>
                      {wyckoffAvailable ? (
                        <>
                          <span
                            data-testid={`wyckoff-phase-${symbolTestId}`}
                            className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700"
                          >
                            {`${wyckoffCycle ? wyckoffCycle.charAt(0).toUpperCase() + wyckoffCycle.slice(1) : "Phase"} ${wyckoffPhase || "—"}`}
                          </span>
                          <span data-testid={`wyckoff-confidence-${symbolTestId}-value`}>
                            {Number.isFinite(wyckoffConfidence) ? `${Math.round(wyckoffConfidence)}% confidence` : "Confidence —"}
                          </span>
                          {wyckoffBottom ? (
                            <span data-testid={`wyckoff-bottom-${symbolTestId}`}>
                              Likely bottom: {_fmtMMMdd(new Date(wyckoffBottom))}
                            </span>
                          ) : null}
                          {wyckoffMacro ? (
                            <span data-testid={`wyckoff-macro-${symbolTestId}`}>Macro: {wyckoffMacro}</span>
                          ) : null}
                        </>
                      ) : (
                        <span data-testid={`wyckoff-empty-${symbolTestId}`} className="text-slate-400">
                          Insufficient history
                        </span>
                      )}
                    </div>
                  </div>
                )}



 

              </div>
            );
            })}
          </div>
        )}
      </main>

      {/* V2 Risk Explain Modal */}
      <Dialog open={!!v2ExplainSymbol} onOpenChange={(open) => { if (!open) setV2ExplainSymbol(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" closeTestId="v2-explain-modal-close">
          <DialogHeader>
            <DialogTitle data-testid="v2-explain-modal-title" className="flex items-center gap-2">
              <span className="font-mono">{v2ExplainSymbol ? getDisplayName(v2ExplainSymbol) : "Ticker"}</span>
              <span className="text-slate-400">•</span>
              <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${getCrashRiskPillClass(v2ExplainSnapshot?.label)}`}>
                {v2ExplainSnapshot ? `${v2ExplainSnapshot.score}% ${v2ExplainSnapshot.label}` : "V2"}
              </span>
            </DialogTitle>
            <DialogDescription data-testid="v2-explain-modal-description" className="text-xs text-slate-500">
              Detailed confluence breakdown for this ticker's Crash Risk V2 score.
            </DialogDescription>
          </DialogHeader>

          {v2ExplainSnapshot ? (
            <div className="space-y-4" data-testid="v2-explain-modal-body">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="v2-cumulative-confidence-card">
                  <p className="text-xs text-slate-500 uppercase">Cumulative Confidence</p>
                  <p className="text-lg font-semibold text-slate-800">{Math.round(Number(v2ExplainSnapshot.cumulative_confidence || 0) * 100)}%</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="v2-remaining-uncertainty-card">
                  <p className="text-xs text-slate-500 uppercase">Remaining Uncertainty</p>
                  <p className="text-lg font-semibold text-slate-800">{Math.round(Number(v2ExplainSnapshot.remaining_uncertainty || 0) * 100)}%</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="v2-trend-flip-penalty-card">
                  <p className="text-xs text-slate-500 uppercase">Trend Flip Penalty</p>
                  <p className="text-lg font-semibold text-slate-800">x{Number(v2ExplainSnapshot.trend_flip_penalty || 1).toFixed(2)}</p>
                </div>
              </div>

              {(localBottomExplainSnapshot || topExplainSnapshot) ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="turning-point-summary-grid">
                  {localBottomExplainSnapshot ? (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="bottom-in-summary-card">
                      <p className="text-xs text-slate-500 uppercase">Local Bottom In</p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${getTurnSignalPillClass(localBottomExplainSnapshot?.label)}`}>
                          {formatLocalBottomLabel(localBottomExplainSnapshot?.label || 'BOTTOM')}
                        </span>
                        <span className="text-sm font-semibold text-slate-800">{Math.round(Number(localBottomExplainSnapshot?.score || 0))}%</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{localBottomExplainSnapshot?.summary || ''}</p>
                    </div>
                  ) : null}
                  {topExplainSnapshot ? (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="top-in-summary-card">
                      <p className="text-xs text-slate-500 uppercase">Top In</p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${getTurnSignalPillClass(topExplainSnapshot?.label)}`}>
                          {topExplainSnapshot?.label || 'TOP'}
                        </span>
                        <span className="text-sm font-semibold text-slate-800">{Math.round(Number(topExplainSnapshot?.score || 0))}%</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{topExplainSnapshot?.summary || ''}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-800" data-testid="v2-gate-breakdown-title">Gate-by-gate contributions</h4>
                <div className="space-y-2">
                  {Object.entries(v2ExplainSnapshot.gates || {}).map(([gateKey, gateValue]) => {
                    const confidencePct = Math.round(Number(gateValue?.confidence || 0) * 100);
                    const weightPct = Math.round(Number((v2ExplainSnapshot.weights || {})[gateKey] || 0) * 100);
                    const isLpplInactive = gateKey === 'lppl' && gateValue?.active === false;

                    return (
                      <div
                        key={gateKey}
                        data-testid={`v2-gate-row-${gateKey}`}
                        className="rounded-lg border border-slate-200 p-3"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">{getGateLabel(gateKey)}</span>
                            <span className="text-[11px] text-slate-500">Weight {weightPct}%</span>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${getGateStateClass(gateValue?.passed)}`}>
                            {isLpplInactive ? 'Inactive' : gateValue?.passed ? 'Risk-aligned' : 'Not-aligned'}
                          </span>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <div className="h-2 flex-1 rounded-full bg-slate-200 overflow-hidden">
                            <div className="h-full bg-slate-700" style={{ width: `${Math.max(0, Math.min(100, confidencePct))}%` }} />
                          </div>
                          <span className="text-xs font-medium text-slate-700" data-testid={`v2-gate-confidence-${gateKey}`}>{confidencePct}%</span>
                        </div>
                        {Array.isArray(gateValue?.reasons) && gateValue.reasons.length > 0 ? (
                          <p className="mt-1 text-xs text-slate-500">{gateValue.reasons.join('; ')}</p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="v2-final-score-summary-card">
                <p className="text-xs text-slate-500 uppercase">Final V2 Summary</p>
                <div className="mt-1 flex flex-wrap items-center gap-2" data-testid="v2-direction-confidence-row">
                  <span
                    data-testid="v2-direction-confidence-badge"
                    className={`px-2 py-0.5 rounded-md text-xs font-medium ${v2ExplainConfidenceMeta.className}`}
                  >
                    Direction confidence: {Number.isFinite(Number(v2ExplainSnapshot?.outlook_confidence)) ? `${Math.round(Number(v2ExplainSnapshot?.outlook_confidence))}%` : 'N/A'} ({v2ExplainConfidenceMeta.label})
                  </span>
                  <span data-testid="v2-direction-confidence-legend" className="text-xs text-slate-500">
                    Legend: 35–50 Low • 51–70 Medium • 71+ High
                  </span>
                </div>
                <p className="text-sm text-slate-700 mt-1" data-testid="v2-final-score-summary-text">
                  {v2ExplainSnapshot.summary_human || v2ExplainSnapshot.summary || 'Confluence summary unavailable.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500" data-testid="v2-explain-modal-empty">V2 breakdown unavailable for this ticker.</div>
          )}
        </DialogContent>
      </Dialog>

      <GraphModal
        selectedGraph={selectedGraph}
        selectedRecord={selectedRecord}
        setSelectedGraph={setSelectedGraph}
        report={report}
        apiBase={API}
        buildAttachmentUrl={buildAttachmentUrl}
        getDirectionIcon={getDirectionIcon}
        getDisplayName={getDisplayName}
        getRiskBadgeClass={getRiskBadgeClass}
        graphAttachmentChoices={graphAttachmentChoices}
      />

      {/* Alert Settings Dialog */}
      <Dialog open={showAlertSettings} onOpenChange={setShowAlertSettings}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" closeTestId="alert-settings-close">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Email Alert Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Enable Email Alerts</p>
                <p className="text-sm text-slate-500">Get notified based on criteria below</p>
              </div>
              <Switch
                checked={alertSettings.enabled}
                onCheckedChange={(checked) => setAlertSettings({ ...alertSettings, enabled: checked })}
              />
            </div>
            
            {alertSettings.enabled && (
              <>
                {/* Email Address */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Alert Email Address</label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={alertSettings.email}
                    onChange={(e) => setAlertSettings({ ...alertSettings, email: e.target.value })}
                  />
                </div>
                
                <div className="border-t border-slate-200 pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-3">Alert Criteria</p>
                  
                  {/* Min Risk Score */}
                  <div className="space-y-2 mb-4">
                    <label className="text-sm text-slate-600">Minimum Risk Score</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4].map((score) => (
                        <button
                          key={score}
                          onClick={() => setAlertSettings({ ...alertSettings, min_risk_score: score })}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                            alertSettings.min_risk_score === score
                              ? score >= 4 ? 'bg-red-500 text-white' 
                                : score >= 3 ? 'bg-orange-500 text-white'
                                : score >= 2 ? 'bg-yellow-500 text-white'
                                : 'bg-green-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400">
                      {alertSettings.min_risk_score === 1 && "Low - Alert for all risk levels"}
                      {alertSettings.min_risk_score === 2 && "Medium - Skip low risk"}
                      {alertSettings.min_risk_score === 3 && "High - Only high & critical"}
                      {alertSettings.min_risk_score === 4 && "Critical only"}
                    </p>
                  </div>
                  
                  {/* Min Confidence Score */}
                  <div className="space-y-2 mb-4">
                    <label className="text-sm text-slate-600">Minimum Confidence Score</label>
                    <div className="flex gap-2">
                      {[0, 0.25, 0.5, 0.75].map((conf) => (
                        <button
                          key={conf}
                          onClick={() => setAlertSettings({ ...alertSettings, min_confidence: conf })}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                            alertSettings.min_confidence === conf
                              ? 'bg-blue-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {conf === 0 ? 'Any' : `≥${conf}`}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400">
                      {alertSettings.min_confidence === 0 && "Alert for any confidence level"}
                      {alertSettings.min_confidence === 0.25 && "Low confidence threshold (≥25%)"}
                      {alertSettings.min_confidence === 0.5 && "Medium confidence threshold (≥50%)"}
                      {alertSettings.min_confidence === 0.75 && "High confidence threshold (≥75%)"}
                    </p>
                  </div>
                  
                  {/* Future TC Only */}
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm text-slate-700">Future TC dates only</p>
                      <p className="text-xs text-slate-400">Ignore past TC predictions</p>
                    </div>
                    <Switch
                      checked={alertSettings.future_only}
                      onCheckedChange={(checked) => setAlertSettings({ ...alertSettings, future_only: checked })}
                    />
                  </div>
                  
                  {/* MLSL Required */}
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p data-testid="mlsl-filter-label" className="text-sm text-slate-700">
                        Require MLSL = True
                      </p>
                      <p data-testid="mlsl-filter-description" className="text-xs text-slate-400">
                        Only MLSL-confirmed TCs
                      </p>
                    </div>
                    <Switch
                      checked={alertSettings.mlsl_required}
                      onCheckedChange={(checked) => setAlertSettings({ ...alertSettings, mlsl_required: checked })}
                    />
                  </div>
                  
                  {/* New Tickers */}
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm text-slate-700">Alert for new tickers</p>
                      <p className="text-xs text-slate-400">Notify when new symbols appear</p>
                    </div>
                    <Switch
                      checked={alertSettings.alert_new_tickers}
                      onCheckedChange={(checked) => setAlertSettings({ ...alertSettings, alert_new_tickers: checked })}
                    />
                  </div>

                  {/* Crash Risk Confidence Labels */}
                  <div className="space-y-2 py-2">
                    <div>
                      <p className="text-sm text-slate-700">Crash Risk Confidence</p>
                      <p className="text-xs text-slate-400">Alert only when the ticker’s Crash Risk label matches (leave empty = allow all)</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {['BUY', 'HOLD', 'PREPARE SELL', 'SELL', 'CRASH ZONE'].map((label) => {
                        const selected = (alertSettings.crash_risk_labels || []).includes(label);
                        const cls =
                          label === 'BUY' ? 'bg-emerald-100 text-emerald-700'
                          : label === 'HOLD' ? 'bg-yellow-100 text-yellow-800'
                          : label === 'PREPARE SELL' ? 'bg-orange-100 text-orange-800'
                          : label === 'SELL' ? 'bg-red-100 text-red-700'
                          : 'bg-red-600 text-white';

                        return (
                          <button
                            key={label}
                            onClick={() => {
                              const current = alertSettings.crash_risk_labels || [];
                              const next = selected
                                ? current.filter((x) => x !== label)
                                : [...current, label];
                              setAlertSettings({ ...alertSettings, crash_risk_labels: next });
                            }}
                            className={`w-full inline-flex items-center justify-center h-7 px-2 rounded-md text-xs font-medium leading-none whitespace-nowrap transition-colors ${cls} ${selected ? 'ring-2 ring-slate-900 ring-offset-2' : 'hover:opacity-90'}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                </div>
              </>
            )}
            
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSaveAlertSettings}
                disabled={savingAlert || (alertSettings.enabled && !alertSettings.email)}
                className="flex-1 bg-slate-900 text-white hover:bg-slate-800"
              >
                {savingAlert ? "Saving..." : "Save Settings"}
              </Button>
              {alertSettings.enabled && alertSettings.email && (
                <Button
                  variant="outline"
                  onClick={handleTestAlert}
                  className="border-slate-200"
                >
                  <Send className="w-4 h-4 mr-1" />
                  Test
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Macro Stack Signal Info */}
      <Dialog
        open={macroStackInfoOpen}
        onOpenChange={(open) => {
          setMacroStackInfoOpen(open);
          if (!open) {
            setMacroStackInfoSignal(null);
          }
        }}
      >
        <DialogContent data-testid="macro-stack-info-dialog" className="max-w-md" closeTestId="macro-stack-info-close">
          <DialogHeader>
            <DialogTitle data-testid="macro-stack-info-title" className="flex items-center gap-2">
              <span>Macro Signal</span>
              <span className="text-slate-500 font-normal">—</span>
              <span>{macroStackInfoSignal?.fullLabel || "Details"}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
              <span data-testid="macro-stack-info-status-label" className="text-xs uppercase text-slate-500">
                Current status
              </span>
              <span
                data-testid="macro-stack-info-status"
                className={`text-xs font-semibold ${macroStackInfoSignal?.active === null || macroStackInfoSignal?.active === undefined ? "text-slate-500" : macroStackInfoSignal?.active ? "text-rose-600" : "text-emerald-600"}`}
              >
                {macroStackInfoSignal?.active === null || macroStackInfoSignal?.active === undefined
                  ? "Not available"
                  : macroStackInfoSignal?.active
                  ? "Active (Bearish bias)"
                  : "Inactive (Bullish bias)"}
              </span>
            </div>
            <div className="space-y-3">
              <div>
                <h4 data-testid="macro-stack-info-bullish-title" className="text-sm font-semibold text-slate-800">
                  Bullish bias
                </h4>
                <p data-testid="macro-stack-info-bullish-text" className="text-sm text-slate-600">
                  {macroStackInfoSignal?.bullish || "—"}
                </p>
              </div>
              <div>
                <h4 data-testid="macro-stack-info-bearish-title" className="text-sm font-semibold text-slate-800">
                  Bearish bias
                </h4>
                <p data-testid="macro-stack-info-bearish-text" className="text-sm text-slate-600">
                  {macroStackInfoSignal?.bearish || "—"}
                </p>
              </div>
              <div>
                <h4 data-testid="macro-stack-info-risk-title" className="text-sm font-semibold text-slate-800">
                  Risk implication
                </h4>
                <p data-testid="macro-stack-info-risk-text" className="text-sm text-slate-600">
                  {macroStackInfoSignal?.risk || "—"}
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-slate-100 mt-auto">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>Financial Analysis Dashboard</span>
            <span>Auto-sync: Daily at 08:00 CET</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
